import {
    extractServerVersion,
    getClientBuildVersion,
    persistClientBuildVersion,
    isVersionMismatch,
} from '@/utils/api/versionApi';
import {createAppUpdateMonitor, readAppUpdateStatus} from '../../../../shared/appUpdateMonitor';
import {setConnectionFailureHandler} from '@/utils/api/connectionFailure';
import {setApiErrorDialogSuppressed} from '@/utils/api/apiErrorDialog';

let monitor;
let onVisibility;
let onOnline;
let onOffline;
let removeConnectionFailureHandler;
function ensureMonitor(context) {
    if (!monitor) {
        monitor = createAppUpdateMonitor({
            clientVersion: context.state.clientVersion,
            readStatus: readAppUpdateStatus,
            reload: () => window.location.reload(),
            onChange: value => {
                context.commit('setReadiness', value);
                const blocked = context.state.isUpdateRequired || value.connectionLost;
                setApiErrorDialogSuppressed(blocked);
                if (blocked && context.rootState.showErrorModal) context.dispatch('hideErrorModal', null, {root: true});
            },
        });
        removeConnectionFailureHandler = setConnectionFailureHandler(() => context.dispatch('reportConnectionLoss'));
        onVisibility = () => { if (!document.hidden) monitor.check(); };
        document.addEventListener('visibilitychange', onVisibility);
        onOnline = () => monitor.check();
        onOffline = () => monitor.reportConnectionLoss();
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        window.addEventListener('focus', onOnline);
    }
    return monitor;
}

export default {
    namespaced: true,
    state: {
        clientVersion: getClientBuildVersion(),
        serverVersion: null,
        isUpdateRequired: false,
        isUpdateModalShown: false,
        status: 'unknown',
        checking: false,
        connectionLost: false,
        confirming: false,
        navigating: false,
        navigationError: false,
        draftEditors: [],
        reconnectionId: 0,
    },
    getters: {
        connectionBlocking(state, getters, rootState, rootGetters) {
            const user = rootGetters.currentUser;
            const owner = user ? `${user.company}:${user.userId}` : '';
            return state.isUpdateRequired || state.connectionLost && !state.draftEditors.some(editor => owner && editor.owner === owner);
        }
    },
    mutations: {
        clearDraftEditors(state) { state.draftEditors = []; },
        registerDraftEditor(state, editor) { state.draftEditors = state.draftEditors.filter(item => item.id !== editor.id).concat(editor); },
        unregisterDraftEditor(state, id) { state.draftEditors = state.draftEditors.filter(item => item.id !== id); },
        setClientVersion(state, version) { state.clientVersion = version; },
        setReadiness(state, value) {
            if (state.connectionLost && !value.connectionLost && value.status === 'ready' && !value.required && !state.isUpdateRequired) state.reconnectionId++;
            state.status = value.status;
            state.checking = value.checking;
            state.connectionLost = value.connectionLost;
            state.confirming = value.confirming;
            state.navigating = value.navigating;
            state.navigationError = value.navigationError;
            state.serverVersion = value.version;
            // Once required, the update cannot be dismissed or undone by a late response.
            state.isUpdateRequired = state.isUpdateRequired || value.required;
            state.isUpdateModalShown = state.isUpdateRequired || state.connectionLost;
        },
    },
    actions: {
        initialize(context) {
            const clientVersion = getClientBuildVersion();
            context.commit('setClientVersion', clientVersion);
            persistClientBuildVersion(clientVersion);
            ensureMonitor(context).start();
            return clientVersion;
        },
        start(context) { return ensureMonitor(context).start(); },
        stop({commit}) {
            if (monitor) monitor.stop();
            if (onVisibility) document.removeEventListener('visibilitychange', onVisibility);
            if (onOnline) { window.removeEventListener('online', onOnline); window.removeEventListener('focus', onOnline); }
            if (onOffline) window.removeEventListener('offline', onOffline);
            commit('clearDraftEditors');
            if (removeConnectionFailureHandler) removeConnectionFailureHandler();
            setApiErrorDialogSuppressed(false);
            monitor = null;
            onVisibility = null;
            removeConnectionFailureHandler = null;
        },
        reportConnectionLoss(context) { return ensureMonitor(context).reportConnectionLoss(); },
        checkNow(context) { return ensureMonitor(context).check(); },
        syncServerVersion(context, headers = {}) {
            const status = headers['x-app-status'];
            const serverVersion = extractServerVersion(headers);
            if ((status && status !== 'ready') ||
                (context.state.clientVersion !== 'dev' && isVersionMismatch(serverVersion, context.state.clientVersion))) {
                ensureMonitor(context).notify(status);
                return true;
            }
            return false;
        },
        syncUpdateError(context, response) {
            const code = response && response.data && response.data.code;
            if (['APP_UPDATE_IN_PROGRESS', 'APP_UPDATE_UNAVAILABLE', 'CLIENT_VERSION_MISMATCH'].includes(code)) {
                ensureMonitor(context).notify(code === 'APP_UPDATE_IN_PROGRESS' ? 'building' : 'unknown');
                return true;
            }
            return false;
        },
        confirmUpdate(context) { return ensureMonitor(context).confirm(); },
    },
};
